import type { AxiosError, AxiosResponse } from 'axios';
import type { CoreApiError } from '@lqa-frontend/api-services';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
} from '@lqa-frontend/api-services';

/**
 * GET /lqaUser/ — paginated list of users with a free-text `name` filter
 * (server-side substring on first/last name). Used by the consultant search
 * picker on the admin-roster page.
 */
export interface LqaUserQueryParams {
	page: number;
	pageSize: number;
	sort?: string;
	name?: string;
	isActive?: boolean;
	accessType?: string;
}

export interface LqaUser {
	id: number;
	isActive: boolean;
	userFirstName: string;
	userLastName: string;
	userEmail: string;
	accessType: string;
	userRole: string;
	isTrainer: boolean;
	initials: string;
	isSchedulable: boolean | null;
	numberOfNights: number | null;
	holidayLocationId: number | null;
	entitlementDays: number | null;
	numberOfCalls: number | null;
	headConsultantGroup: string | null;
}

export interface LqaUsersResponse {
	results: LqaUser[];
	total: number;
	pageSize: number;
	numberOfPages: number;
	page: number;
}

export type LqaUsersQueryOptions = Omit<
	UseQueryOptions<
		AxiosResponse<LqaUsersResponse>,
		AxiosError<CoreApiError>,
		LqaUsersResponse
	>,
	'queryKey' | 'queryFn' | 'select'
>;

export function useLqaUsersQuery(
	params: LqaUserQueryParams,
	options?: LqaUsersQueryOptions
) {
	return useQuery<
		AxiosResponse<LqaUsersResponse>,
		AxiosError<CoreApiError>,
		LqaUsersResponse
	>({
		queryKey: coreApiQueryKey(endpoints.lqaUser.index, 'get', params),
		queryFn: async () =>
			coreApiClient.get<LqaUsersResponse>(endpoints.lqaUser.index, { params }),
		...options,
		select: (response) => response.data,
	});
}
